
import { Order, OrderStatus, Trade, OrderSide, OrderType, TimeInForce, RoutingPreference } from '../types/oms';

class OMSService {
  private orders: Order[] = [
    {
      id: 'ORD-7721',
      symbol: 'NVDA',
      side: 'BUY',
      type: 'LIMIT',
      quantity: 500,
      filledQuantity: 200,
      price: 892.50,
      timeInForce: 'GTC',
      routing: 'SMART',
      status: 'PARTIALLY_FILLED',
      timestamp: new Date(Date.now() - 1000 * 60 * 15).toISOString(),
      updatedAt: new Date(Date.now() - 1000 * 60 * 5).toISOString(),
      averageFillPrice: 892.45
    },
    {
      id: 'ORD-7722',
      symbol: 'AAPL',
      side: 'SELL',
      type: 'MARKET',
      quantity: 1000,
      filledQuantity: 1000,
      timeInForce: 'DAY',
      routing: 'NASDAQ',
      status: 'FILLED',
      timestamp: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
      updatedAt: new Date(Date.now() - 1000 * 60 * 29).toISOString(),
      averageFillPrice: 189.42
    },
    {
      id: 'ORD-7723',
      symbol: 'TSLA',
      side: 'BUY',
      type: 'STOP',
      quantity: 250,
      filledQuantity: 0,
      stopPrice: 180.00,
      timeInForce: 'DAY',
      routing: 'ARCA',
      status: 'OPEN',
      timestamp: new Date(Date.now() - 1000 * 60 * 5).toISOString(),
      updatedAt: new Date(Date.now() - 1000 * 60 * 5).toISOString(),
    }
  ];

  getOrders(): Order[] {
    return [...this.orders];
  }

  validateOrder(order: Partial<Order>): { valid: boolean; error?: string } {
    if (!order.symbol) return { valid: false, error: 'Symbol is required' };
    if (!order.quantity || order.quantity <= 0) return { valid: false, error: 'Invalid quantity' };
    if (order.type !== 'MARKET' && (!order.price && !order.stopPrice)) return { valid: false, error: 'Price is required for non-market orders' };
    
    // Pre-trade risk checks (Mock)
    if (order.quantity > 10000) return { valid: false, error: 'Order size exceeds single-order limit' };
    
    return { valid: true };
  }

  submitOrder(orderData: Omit<Order, 'id' | 'status' | 'timestamp' | 'updatedAt' | 'filledQuantity'>): Order {
    const validation = this.validateOrder(orderData);
    if (!validation.valid) {
      throw new Error(validation.error);
    }

    const newOrder: Order = {
      ...orderData,
      id: `ORD-${Math.floor(Math.random() * 10000)}`,
      status: 'OPEN',
      filledQuantity: 0,
      timestamp: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    this.orders.unshift(newOrder);
    return newOrder;
  }

  cancelOrder(orderId: string): Order {
    const orderIndex = this.orders.findIndex(o => o.id === orderId);
    if (orderIndex === -1) throw new Error('Order not found');
    
    const order = this.orders[orderIndex];
    if (['FILLED', 'CANCELLED', 'REJECTED'].includes(order.status)) {
      throw new Error('Order cannot be cancelled in current status');
    }

    const updatedOrder: Order = {
      ...order,
      status: 'CANCELLED',
      updatedAt: new Date().toISOString()
    };

    this.orders[orderIndex] = updatedOrder;
    return updatedOrder;
  }

  modifyOrder(orderId: string, updates: Partial<Order>): Order {
    const orderIndex = this.orders.findIndex(o => o.id === orderId);
    if (orderIndex === -1) throw new Error('Order not found');

    const order = this.orders[orderIndex];
    const updatedOrder: Order = {
      ...order,
      ...updates,
      updatedAt: new Date().toISOString()
    };

    this.orders[orderIndex] = updatedOrder;
    return updatedOrder;
  }
}

export const omsService = new OMSService();
